import type { PortfolioContent } from './types';

export const defaultPortfolioContent: PortfolioContent = {
    settings: {
        home: {
            eyebrow: 'Computer Engineer · AI & Software',
            title: 'Engineering useful products with clarity and purpose.',
            introduction:
                'I work across software development, data science and artificial intelligence, turning complex challenges into practical, maintainable solutions.',
            primaryAction: { label: 'Explore my profile', href: '/about' },
            secondaryAction: { label: 'Let’s connect', href: '/contact' },
            currentFocusEyebrow: 'Current focus',
            currentFocus:
                'Research and development at the intersection of engineering and intelligent software.',
            focusDetails: [
                {
                    id: 'core-areas',
                    label: 'Core areas',
                    value: 'AI · Data · Applications',
                },
                {
                    id: 'approach',
                    label: 'Approach',
                    value: 'Research-driven and user-focused',
                },
            ],
            areasHeader: {
                eyebrow: 'What I do',
                title: 'A broad technical foundation, applied with focus.',
                description:
                    'My experience connects engineering fundamentals with modern software and data tools.',
            },
            focusAreas: [
                {
                    id: 'intelligent-systems',
                    title: 'Intelligent systems',
                    description:
                        'Machine learning, artificial intelligence and data workflows designed around real problems.',
                    iconKey: 'brain',
                },
                {
                    id: 'software-development',
                    title: 'Software development',
                    description:
                        'Responsive frontend, backend and mobile applications built with a practical engineering approach.',
                    iconKey: 'code',
                },
                {
                    id: 'end-to-end-thinking',
                    title: 'End-to-end thinking',
                    description:
                        'From requirements and research to implementation, testing and continuous improvement.',
                    iconKey: 'layers',
                },
            ],
        },
        about: {
            header: {
                eyebrow: 'Profile',
                title: 'About me',
                description:
                    'A multidisciplinary engineer interested in software, data and intelligent systems.',
            },
            greeting: 'Hello, I’m',
            fullName: 'Helder Henrique da Silva',
            role: 'Computer Engineer',
            biography: [
                'Currently pursuing a degree in Computer Engineering, I have worked since 2022 in research and development related to data science, machine learning, artificial intelligence and Python application development.',
                'I also have practical experience in frontend, backend and mobile development, creating responsive applications and seeking challenges that combine creative problem-solving with advanced technology.',
            ],
            profileImageAlt: 'Portrait of Helder Henrique',
            journeyTitle: 'More about my journey',
            tabs: [
                {
                    id: 'interests',
                    title: 'Interests & Hobbies',
                    paragraphs: [
                        'In my free time, I love immersing myself in activities that challenge both my mind and body. I enjoy coding experiments, gaming, hiking and science fiction. Artificial intelligence and machine learning are also enduring interests that continuously inspire me to learn and innovate.',
                    ],
                    facts: [],
                },
                {
                    id: 'course',
                    title: 'About My Course',
                    paragraphs: [
                        'Computer Engineering — Universidade Federal de Santa Catarina',
                        'Computer Engineering combines Computer Science, engineering, mathematics, electrical engineering and digital electronics. The course explores how hardware and software come together in embedded computational systems and intelligent products.',
                    ],
                    facts: [
                        {
                            id: 'activity-hours',
                            label: 'Activity hours',
                            value: 'Day and night, full-time',
                        },
                        { id: 'mec-concept', label: 'MEC concept', value: '4' },
                        { id: 'enade', label: 'ENADE', value: '5 out of 5' },
                        {
                            id: 'workload',
                            label: 'Total workload',
                            value: '3,600 hours',
                        },
                    ],
                },
                {
                    id: 'oath',
                    title: 'Oath',
                    paragraphs: [
                        'I promise to honor the profession of computer engineer, strive for the development and application of science and technology, observe professional ethics, respect nature and place my scientific knowledge at the service of humanity.',
                    ],
                    facts: [],
                },
            ],
            skillsHeader: {
                eyebrow: 'Toolkit',
                title: 'Skills',
                description:
                    'Technologies, practices and capabilities developed through academic, professional and personal projects.',
            },
        },
        academicHeader: {
            eyebrow: 'Education',
            title: 'Academic history',
            description:
                'A path across engineering and technology, with a foundation in both software and the physical systems it supports.',
        },
        professionalHeader: {
            eyebrow: 'Experience',
            title: 'Professional history',
            description:
                'Research, software development, project management and engineering experiences that shaped how I approach complex problems.',
        },
        projectsPage: {
            header: {
                eyebrow: 'Selected work',
                title: 'Projects',
                description:
                    'A curated project showcase is being prepared with context, technical decisions and measurable outcomes.',
            },
            emptyState: {
                badge: 'In progress',
                title: 'The complete project archive is available on GitHub.',
                description:
                    'Until the case studies are published here, explore repositories, experiments and ongoing work directly on my profile.',
                action: {
                    label: 'GitHub projects',
                    href: 'https://github.com/theHprogrammer',
                },
            },
        },
        certificationsPage: {
            header: {
                eyebrow: 'Continuous learning',
                title: 'Certifications',
                description:
                    'Courses and credentials that complement my academic background and practical experience.',
            },
            emptyState: {
                badge: 'Catalog in progress',
                title: 'Verified credentials are available on LinkedIn.',
                description:
                    'The dedicated catalog will be added here. For now, LinkedIn contains the current certificates and issuing organizations.',
                action: {
                    label: 'View certifications',
                    href: 'https://www.linkedin.com/in/thehprogrammer/details/certifications/',
                },
            },
        },
        contactPage: {
            header: {
                eyebrow: 'Get in touch',
                title: 'Contact me',
                description:
                    'Choose the most convenient channel for professional opportunities, collaboration or a conversation about technology.',
            },
            cardActionLabel: 'Open profile',
            creditsTitle: 'Credits',
            creditsDescription:
                'Attribution for third-party icons used on this page.',
        },
    },
    academicEntries: [
        {
            id: 'academic-ufsc',
            period: '2021 — Present',
            title: 'Computer Engineering Undergraduate',
            subtitle: 'UFSC — Federal University of Santa Catarina',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: ['Academic Performance Index: 8.80'],
            iconKey: 'university',
            order: 10,
        },
        {
            id: 'academic-unesp',
            period: '2018 — 2020',
            title: 'Civil Engineering Undergraduate',
            subtitle:
                'UNESP — São Paulo State University “Júlio de Mesquita Filho”',
            location: 'Ilha Solteira, São Paulo, Brazil',
            descriptions: [
                'Interrupted for external transfer.',
                'Academic Performance Index: 7.69',
            ],
            iconKey: 'university',
            order: 20,
        },
        {
            id: 'academic-etec',
            period: '2016 — 2017',
            title: 'Technical in Building Construction',
            subtitle: 'Etec Philadelpho Gouvêa Netto',
            location: 'São José do Rio Preto, São Paulo, Brazil',
            descriptions: [
                'Professional technical qualification as an Assistant Technician in Building Installations.',
            ],
            iconKey: 'school',
            order: 30,
        },
        {
            id: 'academic-high-school',
            period: '2013 — 2015',
            title: 'High School',
            subtitle: 'E.E. Profª Amira Homsi Chalella',
            location: 'São José do Rio Preto, São Paulo, Brazil',
            descriptions: [],
            iconKey: 'school',
            order: 40,
        },
    ],
    professionalEntries: [
        {
            id: 'professional-analytics2ai',
            period: 'Nov 2023 — Present',
            title: 'P&D Analytics2AI',
            subtitle:
                'Research & Development · Liga Acadêmica de Inteligência Artificial',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Study group focused on business consultancy.',
                'Research and development of techniques, methods and tools for data analysis, machine learning and artificial intelligence.',
            ],
            iconKey: 'briefcase',
            order: 10,
        },
        {
            id: 'professional-medical-ai',
            period: 'Mar 2023 — Mar 2024',
            title: 'Mobile Developer (Flutter)',
            subtitle: 'Intern · Medical AI',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Assisted in the development and testing of mobile applications.',
                'Researched expert and generative AI for medication recommendation.',
            ],
            iconKey: 'briefcase',
            order: 20,
        },
        {
            id: 'professional-labtec',
            period: 'Dec 2021 — Sep 2022',
            title: 'Team Manager and Designer',
            subtitle:
                'Scholar · LabTeC — Computational Technologies Laboratory, UFSC',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Organized meetings, monitored team activities and documented agendas and minutes.',
                'Created project assets and worked with image processing.',
            ],
            iconKey: 'briefcase',
            order: 30,
        },
        {
            id: 'professional-consultant',
            period: 'Dec 2021 — Jan 2023',
            title: 'Consultant',
            subtitle:
                'Volunteer · EJEC — Junior Enterprise of Computer Engineering',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Consultancy and advisory support in general management activities.',
            ],
            iconKey: 'briefcase',
            order: 40,
        },
        {
            id: 'professional-project-manager',
            period: 'Jun 2021 — Dec 2021',
            title: 'Project Manager',
            subtitle:
                'Volunteer · EJEC — Junior Enterprise of Computer Engineering',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Managed projects, teams, knowledge and the project pipeline using an adapted Scrum methodology.',
                'Participated in board meetings and supported project advisory activities.',
            ],
            iconKey: 'briefcase',
            order: 50,
        },
        {
            id: 'professional-project-advisor',
            period: 'Feb 2021 — Jun 2021',
            title: 'Project Advisor',
            subtitle:
                'Volunteer · EJEC — Junior Enterprise of Computer Engineering',
            location: 'Araranguá, Santa Catarina, Brazil',
            descriptions: [
                'Collected requirements and business rules, developed scopes and met with project stakeholders.',
                'Monitored active projects and researched opportunities for innovation.',
            ],
            iconKey: 'briefcase',
            order: 60,
        },
        {
            id: 'professional-icec',
            period: 'Aug 2016 — Aug 2017',
            title: 'Project Intern',
            subtitle: 'Intern · GRUPO ICEC',
            location: 'Mirassol, São Paulo, Brazil',
            descriptions: [
                'Detailed metal structures and prepared project plots for verification.',
                'Created engineering checklists in Excel.',
            ],
            iconKey: 'briefcase',
            order: 70,
        },
    ],
    skillGroups: [
        {
            id: 'skills-languages',
            title: 'Languages',
            order: 10,
            skills: [
                {
                    id: 'python',
                    iconKey: 'python',
                    name: 'Python',
                    level: 'Advanced',
                },
                {
                    id: 'cpp',
                    iconKey: 'cplusplus',
                    name: 'C/C++',
                    level: 'Intermediate',
                },
                {
                    id: 'dart',
                    iconKey: 'flutter',
                    name: 'Dart',
                    level: 'Intermediate',
                },
                {
                    id: 'javascript',
                    iconKey: 'javascript',
                    name: 'JavaScript',
                    level: 'Basic',
                },
                {
                    id: 'typescript',
                    iconKey: 'typescript',
                    name: 'TypeScript',
                    level: 'Basic',
                },
                { id: 'vhdl', name: 'VHDL', level: 'Basic' },
            ],
        },
        {
            id: 'skills-frameworks',
            title: 'Frameworks',
            order: 20,
            skills: [
                {
                    id: 'django',
                    iconKey: 'django',
                    name: 'Django',
                    level: 'Advanced',
                },
                {
                    id: 'fastapi',
                    iconKey: 'fastapi',
                    name: 'FastAPI',
                    level: 'Intermediate',
                },
                {
                    id: 'flask',
                    iconKey: 'flask',
                    name: 'Flask',
                    level: 'Intermediate',
                },
                {
                    id: 'flutter',
                    iconKey: 'flutter',
                    name: 'Flutter',
                    level: 'Intermediate',
                },
                {
                    id: 'react',
                    iconKey: 'react',
                    name: 'React',
                    level: 'Basic',
                },
                {
                    id: 'nodejs',
                    iconKey: 'nodejs',
                    name: 'Node.js',
                    level: 'Basic',
                },
                {
                    id: 'tailwind',
                    iconKey: 'tailwindcss',
                    name: 'Tailwind CSS',
                    level: 'Basic',
                },
            ],
        },
        {
            id: 'skills-databases',
            title: 'Databases',
            order: 30,
            skills: [
                {
                    id: 'mysql',
                    iconKey: 'mysql',
                    name: 'MySQL',
                    level: 'Advanced',
                },
                {
                    id: 'postgresql',
                    iconKey: 'postgresql',
                    name: 'PostgreSQL',
                    level: 'Advanced',
                },
                {
                    id: 'mongodb',
                    iconKey: 'mongodb',
                    name: 'MongoDB',
                    level: 'Intermediate',
                },
                {
                    id: 'elasticsearch',
                    iconKey: 'elasticsearch',
                    name: 'Elasticsearch',
                    level: 'Intermediate',
                },
                { id: 'qdrant', name: 'Qdrant', level: 'Intermediate' },
            ],
        },
        {
            id: 'skills-data-ai',
            title: 'Data Science & AI',
            order: 40,
            skills: [
                {
                    id: 'pandas',
                    iconKey: 'pandas',
                    name: 'Pandas',
                    level: 'Advanced',
                },
                {
                    id: 'numpy',
                    iconKey: 'numpy',
                    name: 'NumPy',
                    level: 'Advanced',
                },
                {
                    id: 'seaborn',
                    iconKey: 'python',
                    name: 'Seaborn',
                    level: 'Advanced',
                },
                {
                    id: 'jupyter',
                    iconKey: 'jupyter',
                    name: 'Jupyter',
                    level: 'Advanced',
                },
                {
                    id: 'matplotlib',
                    iconKey: 'python',
                    name: 'Matplotlib',
                    level: 'Advanced',
                },
                {
                    id: 'tensorflow',
                    iconKey: 'tensorflow',
                    name: 'TensorFlow',
                    level: 'Intermediate',
                },
                {
                    id: 'keras',
                    iconKey: 'keras',
                    name: 'Keras',
                    level: 'Intermediate',
                },
                {
                    id: 'pytorch',
                    iconKey: 'pytorch',
                    name: 'PyTorch',
                    level: 'Intermediate',
                },
                {
                    id: 'opencv',
                    iconKey: 'opencv',
                    name: 'OpenCV',
                    level: 'Intermediate',
                },
                {
                    id: 'nltk',
                    iconKey: 'python',
                    name: 'NLTK',
                    level: 'Intermediate',
                },
            ],
        },
        {
            id: 'skills-devops',
            title: 'DevOps & Tools',
            order: 50,
            skills: [
                { id: 'git', iconKey: 'git', name: 'Git', level: 'Advanced' },
                {
                    id: 'github',
                    iconKey: 'github',
                    name: 'GitHub',
                    level: 'Intermediate',
                },
                {
                    id: 'linux',
                    iconKey: 'linux',
                    name: 'Linux',
                    level: 'Intermediate',
                },
                {
                    id: 'docker',
                    iconKey: 'docker',
                    name: 'Docker',
                    level: 'Intermediate',
                },
                {
                    id: 'kubernetes',
                    iconKey: 'kubernetes',
                    name: 'Kubernetes',
                    level: 'Intermediate',
                },
            ],
        },
        {
            id: 'skills-communication',
            title: 'Communication',
            order: 60,
            skills: [
                { id: 'communication', name: 'Communication' },
                { id: 'teamwork', name: 'Teamwork' },
                { id: 'resilience', name: 'Resilience' },
                { id: 'conflict-resolution', name: 'Conflict Resolution' },
            ],
        },
        {
            id: 'skills-methodologies',
            title: 'Methodologies',
            order: 70,
            skills: [
                { id: 'scrum', name: 'Scrum' },
                { id: 'kanban', name: 'Kanban' },
                { id: 'xp', name: 'Extreme Programming' },
            ],
        },
        {
            id: 'skills-spoken-languages',
            title: 'Languages spoken',
            order: 80,
            skills: [
                { id: 'english', name: 'English', level: 'Intermediate' },
                { id: 'portuguese', name: 'Portuguese', level: 'Native' },
                { id: 'libras', name: 'Libras', level: 'Basic' },
            ],
        },
        {
            id: 'skills-other',
            title: 'Other',
            order: 90,
            skills: [
                {
                    id: 'wordpress',
                    iconKey: 'wordpress',
                    name: 'WordPress',
                    level: 'Intermediate',
                },
                { id: 'openai', name: 'OpenAI', level: 'Advanced' },
            ],
        },
    ],
    contactLinks: [
        {
            id: 'contact-linkedin',
            title: 'LinkedIn',
            url: 'https://www.linkedin.com/in/theHprogrammer',
            iconKey: 'linkedin',
            order: 10,
        },
        {
            id: 'contact-github',
            title: 'GitHub',
            url: 'https://github.com/theHprogrammer',
            iconKey: 'github',
            order: 20,
        },
        {
            id: 'contact-email',
            title: 'Email',
            url: 'mailto:helder.hen.silva@gmail.com',
            iconKey: 'email',
            order: 30,
        },
        {
            id: 'contact-whatsapp',
            title: 'WhatsApp',
            url: 'https://wa.me/+5517992288143',
            iconKey: 'whatsapp',
            order: 40,
        },
        {
            id: 'contact-lattes',
            title: 'Lattes',
            url: 'http://lattes.cnpq.br/2072583923430990',
            iconKey: 'lattes',
            order: 50,
        },
        {
            id: 'contact-youtube',
            title: 'YouTube',
            url: 'https://www.youtube.com/channel/UC0oSqkRhIz82pcjx1WRIZnA',
            iconKey: 'youtube',
            order: 60,
        },
        {
            id: 'contact-facebook',
            title: 'Facebook',
            url: 'https://www.facebook.com/HHS.Helder',
            iconKey: 'facebook',
            order: 70,
        },
        {
            id: 'contact-instagram',
            title: 'Instagram',
            url: 'https://www.instagram.com/hhs_hen/',
            iconKey: 'instagram',
            order: 80,
        },
        {
            id: 'contact-spotify',
            title: 'Spotify',
            url: 'https://open.spotify.com/user/31jogjcje3s3vtvk7ryqyhdr5hy4?si=537f69e9d8b745fb',
            iconKey: 'spotify',
            order: 90,
        },
        {
            id: 'contact-discord',
            title: 'Discord',
            url: 'https://discord.gg/reuHeBFQkd',
            iconKey: 'discord',
            order: 100,
        },
    ],
    iconCredits: [
        {
            id: 'credit-email',
            label: 'Email icon by Freepik',
            url: 'https://www.flaticon.com/br/icones-gratis/enviar',
            order: 10,
        },
        {
            id: 'credit-github',
            label: 'GitHub icon by Pixel perfect',
            url: 'https://www.flaticon.com/br/icones-gratis/github',
            order: 20,
        },
        {
            id: 'credit-linkedin',
            label: 'LinkedIn icon by Freepik',
            url: 'https://www.flaticon.com/br/icones-gratis/linkedin',
            order: 30,
        },
        {
            id: 'credit-whatsapp',
            label: 'WhatsApp icon by Fathema Khanom',
            url: 'https://www.flaticon.com/br/icones-gratis/whatsapp',
            order: 40,
        },
        {
            id: 'credit-youtube',
            label: 'YouTube icon by Md Tanvirul Haque',
            url: 'https://www.flaticon.com/br/icones-gratis/youtube',
            order: 50,
        },
        {
            id: 'credit-facebook',
            label: 'Facebook icon by Freepik',
            url: 'https://www.flaticon.com/br/icones-gratis/facebook',
            order: 60,
        },
        {
            id: 'credit-instagram',
            label: 'Instagram icon by Laisa Islam Ani',
            url: 'https://www.flaticon.com/br/icones-gratis/logotipo-do-instagram',
            order: 70,
        },
        {
            id: 'credit-spotify',
            label: 'Spotify icon by Freepik',
            url: 'https://www.flaticon.com/br/icones-gratis/spotify',
            order: 80,
        },
        {
            id: 'credit-discord',
            label: 'Discord icon by Hight Quality Icons',
            url: 'https://www.flaticon.com/br/icones-gratis/discordia',
            order: 90,
        },
    ],
    projects: [],
    certifications: [],
};
